import { Card, CardContent } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChunkMessage } from "@/constants/enums";
import { cn } from "@/lib/utils";
import { ChevronRightIcon, ChevronDownIcon, CheckIcon } from "lucide-react";
import { RingsLoader } from "../../loaders";
import { useTranslation } from "react-i18next";
import { Dispatch, SetStateAction, useMemo } from "react";
import ActiveImportStream from "./ActiveImportStream";
import { useAiImportRuntime } from "@/store/aiImportRuntime";
import { createLocalizedStepContentGetter } from "./utils";
import { STEP_KEYS } from "@/utils/aiImport";

/**
 * Props interface for the ActiveImportCard component
 */
interface Props {
  /** Unique identifier for the AI import process */
  aiImportId: number;
  /** Whether the card is currently expanded to show detailed steps */
  isExpanded: boolean;
  /** Function to update the expanded state of import cards */
  setIsExpanded: Dispatch<SetStateAction<{ [key: number]: boolean }>>;
  /** Name of the publication being imported */
  currentPublicationName: string;
  /** Index of the last completed/active step (0-based) */
  lastIndex: number;
  /** Overall progress percentage (0-100) */
  progress: number;
  /** Array of step objects containing step information */
  displaySteps: any[];
}

// Development mode flag for showing debug components
const isDevMode = import.meta.env.VITE_APP_ENV === "dev";

/**
 * ActiveImportCard displays the progress and status of an AI import process.
 * Features a collapsible design showing summary view with expandable detailed steps.
 *
 * @component
 * @example
 * // Basic usage
 * <ActiveImportCard
 *   aiImportId={123}
 *   isExpanded={false}
 *   setIsExpanded={setExpandedState}
 *   currentPublicationName="Sample Publication"
 *   lastIndex={2}
 *   progress={45}
 *   displaySteps={steps}
 *   getLocalizedStepContent={getStepContent}
 * />
 *
 * @example
 * // With completed import
 * <ActiveImportCard
 *   aiImportId={124}
 *   isExpanded={true}
 *   setIsExpanded={setExpandedState}
 *   currentPublicationName="Completed Publication"
 *   lastIndex={8}
 *   progress={100}
 *   displaySteps={completedSteps}
 *   getLocalizedStepContent={getStepContent}
 * />
 */
const ActiveImportCard = (props: Props) => {

  // Props
  const {
    aiImportId, isExpanded, setIsExpanded,
    currentPublicationName, lastIndex, progress,
    displaySteps
  } = props;

  // Hooks
  const { t } = useTranslation(["components", "common"]);
  const activeImports = useAiImportRuntime((state) => state.activeImports);

  const hasSteps = (activeImports[aiImportId]?.steps?.length ?? 0) > 0;
  // Show "Fetching latest steps" only for explicitly marked resuming imports
  const shouldShowFetching = !hasSteps && (activeImports[aiImportId]?.isResuming ?? false);

  // Create the localized step content getter function
  const getLocalizedStepContent = useMemo(() =>
    createLocalizedStepContentGetter(t, STEP_KEYS),
  [t]
  );

  return (
    <Card key={aiImportId} className="bg-gray-50 border-none shadow-none">
      <Collapsible
        open={isExpanded}
        onOpenChange={(open) => setIsExpanded((prev) => ({ ...prev, [aiImportId]: open }))}
      >
        <CollapsibleTrigger asChild>
          <CardContent className="flex flex-col gap-1 px-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {/* Chevron icons indicate expansion state - right when expanded, down when collapsed */}
                {isExpanded ? (
                  <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                )}
                {currentPublicationName ? (
                  // Responsive text truncation based on screen size
                  <h3 className="font-medium text-sm max-w-[200px] md:max-w-[350px] lg:max-w-[400px] xl:max-w-[500px] truncate">
                    {currentPublicationName}
                  </h3>
                ) : (
                  <h3 className="font-thin text-sm">
                    {t("common:notavailable")}
                  </h3>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Step counter with max limit of 9 steps */}
                <span className="text-xs text-gray-500">Step {Math.min(lastIndex + 1, 9)}/9</span>
                {/* Development-only stream debugging component */}
                {isDevMode && (
                  <ActiveImportStream aiImportId={aiImportId} />
                )}
              </div>
            </div>

            {/* Summary view: Show current step info only when collapsed and has steps */}
            {/* (always show a step title when collapsed) */}
            {!isExpanded && (
              <div className="flex items-center gap-1 mb-2">
                {progress === 100 ? (
                  <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center shrink-0">
                    <CheckIcon className="w-2.5 h-2.5 text-white" />
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <RingsLoader
                      visible={true}
                      height={20}
                      width={20}
                      color="#2563EB"
                      ariaLabel="rings-loading"
                      radius={1}
                    />
                  </div>
                )}
                <span className="text-sm text-gray-700 font-medium">
                  {shouldShowFetching
                    ? t("aiimportloader.fetchinglateststeps")
                    : lastIndex >= 0
                      ? getLocalizedStepContent(lastIndex, ChunkMessage.TITLE)
                      : getLocalizedStepContent(0, ChunkMessage.TITLE)} {/* defaults to "Initializing" */}
                </span>
              </div>
            )}

            {/* Progress bar */}
            <div>
              <div className="relative">
                <div className="w-full h-[20px] bg-gray-100 rounded">
                  <div
                    className={cn("h-[20px] rounded", progress === 100 ? "bg-green-200" : "bg-blue-200")}
                    style={{ width: `${progress}%` }}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className={cn("text-xs font-medium", progress === 100 ? "text-green-500" : "text-blue-500")}>
                        {progress}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </CollapsibleTrigger>

        {/* Detailed steps view - only visible when expanded */}
        <CollapsibleContent>
          <CardContent className="pt-0 pb-4 px-4">
            <div className="space-y-2 border-t pt-4">
              <h4 className="font-medium text-sm text-gray-700 mb-3">
                {t("aiimportloader.detailedsteps")}
              </h4>
              {displaySteps.map((step, index) => {
                // Determine step status based on current progress
                const isActive = index === lastIndex && step.type === "PROGRESS";
                const isCompleted = index < lastIndex || progress === 100;
                const isPending = index > lastIndex;

                return (
                  <div
                    key={`${step.id}-${step.type}-${index}`}
                    className="relative flex items-start gap-3 p-2 hover:bg-gray-100 rounded-lg"
                  >
                    {/* Step status indicator - positioned at top to align with title */}
                    <div className="shrink-0 mt-1">
                      {isActive ? (
                        // Currently active step: show loading spinner
                        <RingsLoader
                          visible={true}
                          height={20}
                          width={20}
                          color="#2563EB"
                          ariaLabel="rings-loading"
                          radius={1}
                        />
                      ) : isCompleted ? (
                        // Completed step: green circle with checkmark
                        <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      ) : isPending ? (
                        // Pending step: gray circle with dot
                        <div className="w-5 h-5 bg-gray-300 rounded-full flex items-center justify-center">
                          <div className="w-2 h-2 bg-gray-500 rounded-full"></div>
                        </div>
                      ) : (
                        // Default/unknown state: light blue circle
                        <div className="w-5 h-5 bg-blue-200 rounded-full"></div>
                      )}
                    </div>

                    {/* Step content section */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-sm text-gray-800 truncate">
                          {step.title}
                        </h4>
                        {/* Status badge with conditional styling based on step state */}
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isPending
                            ? "bg-gray-100 text-gray-500"
                            : isCompleted
                              ? "bg-green-100 text-green-700"
                              : "bg-blue-100 text-blue-700"}`}
                        >
                          {/* Localized status text based on step state */}
                          {isPending ?
                            t("aiimportloader.progress.pending") :
                            isCompleted ?
                              t("aiimportloader.progress.completed") :
                              t("aiimportloader.progress.inprogress")
                          }
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {step.content}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

export default ActiveImportCard;
